import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import ApiService from '../../ApiService';
import { Box, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { SasAccount } from '../../types/SasAccount';

export interface SasAccountProps {
    disabled?: boolean;
    sasAccountId: number | undefined;
    onInit?: (accountId: number, account: SasAccount | null) => void;
    onSelectSasAccount(accountId: number, account: SasAccount | null): void;
    error?: boolean;
    variant?: 'filled' | 'outlined';
    shrinkLabel?: boolean;
}

export default function SasAccountComponent({
    disabled,
    error,
    onInit,
    onSelectSasAccount,
    sasAccountId,
    shrinkLabel,
    variant = 'outlined',
}: SasAccountProps) {
    const [accounts, setAccounts] = useState<SasAccount[]>([]);
    const initRef = useRef<boolean>(false);
    const onSelectSasAccountRef = useRef(onSelectSasAccount);

    const getAccount = useCallback(
        (accountId: number): SasAccount | null => {
            return accounts.find((_account: SasAccount) => _account.id === accountId) || null;
        },
        [accounts]
    );

    // onSelectSasAccount is being set in the parent component as an inline function which will
    // trigger too many updates/side effects.  Use a ref to isolate those changes until the parent
    // components can be refactored.
    useEffect(() => {
        onSelectSasAccountRef.current = onSelectSasAccount;
    }, [onSelectSasAccount]);

    useEffect(() => {
        ApiService.getSasAccounts()
            .then((response) => {
                setAccounts(response.data);
                if (response.data.length === 1) {
                    onSelectSasAccountRef.current(response.data[0].id, response.data[0]);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (initRef.current === false && accounts.length > 0) {
            initRef.current = true;

            if (onInit) {
                onInit(sasAccountId || 0, getAccount(sasAccountId || 0));
            }
        }
    }, [accounts, sasAccountId, onInit, getAccount]);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const id = Number(event.target.value);
            onSelectSasAccountRef.current(id, getAccount(id));
        },
        [getAccount]
    );

    return (
        <>
            <div id="sasAccount" style={{ width: '100%' }}>
                <TextField
                    variant={variant}
                    fullWidth
                    size="small"
                    label={disabled ? 'Ad Server Account' : 'Select Ad Server Account'}
                    name="accountId"
                    select
                    disabled={disabled}
                    required={!disabled}
                    value={sasAccountId != null ? String(sasAccountId) : ''}
                    error={error}
                    helperText={error ? 'Account is required.' : ''}
                    onChange={handleChange}
                    slotProps={{
                        select: {
                            native: false,
                            IconComponent: disabled ? () => null : undefined,
                        },
                        inputLabel: { shrink: shrinkLabel },
                        input: disabled
                            ? {
                                  endAdornment: (
                                      <InputAdornment position="end">
                                          <Box
                                              sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                              }}
                                          >
                                              <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ fontSize: '0.75rem' }}
                                              >
                                                  (Read Only)
                                              </Typography>
                                              <InfoOutlined
                                                  sx={{
                                                      fontSize: '1rem',
                                                      color: 'text.secondary',
                                                  }}
                                              />
                                          </Box>
                                      </InputAdornment>
                                  ),
                              }
                            : undefined,
                    }}
                    sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                        },
                    }}
                >
                    {accounts.map((account) => {
                        return (
                            <MenuItem key={String(account.id)} value={String(account.id)}>
                                {account.accountName}
                            </MenuItem>
                        );
                    })}
                </TextField>
            </div>
        </>
    );
}
